import React, { FC, useEffect, useState } from "react";
import Loader from "@components/Loader/Loader";
import {
  useGetAccountsQuery,
  useUpdateAccountMutation,
} from "@store/accountApi";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AccountForm from "@components/AccountForm/AccountForm";
import { FieldValues, useForm } from "react-hook-form";
import { Account } from "@models/account";
import HomeForm from "@components/Home/HomeForm/HomeForm";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import EmailForm from "@components/EmailForm/EmailForm";
import TextIconButton from "@components/TextIconButton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import { setMessage } from "@store/snackbarSlice";
import { useGetUserQuery, useUpdateUserMutation } from "@store/userApi";
import { User } from "@models/user";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const MyAccount: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: accounts } = useGetAccountsQuery();
  const { data: user } = useGetUserQuery();
  const email = useSelector((state: RootState) => state.auth.user?.email);
  const [updateAccount] = useUpdateAccountMutation();
  const [updateUser] = useUpdateUserMutation();
  const [isSaving, setIsSaving] = useState(false);

  const accountForm = useForm<Partial<Account & User>>({
    mode: "onChange",
    criteriaMode: "all",
  });
  const emailForm = useForm<{ email: string }>({ disabled: true });

  const {
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
    reset,
  } = accountForm;

  const updateUserDetails = async (user: Partial<User>) => {
    const userRes = await updateUser(user);

    if (userRes.error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));

      return false;
    }

    return true;
  };

  const updateAccountDetails = async (account: Partial<Account>) => {
    const accRes = await updateAccount({
      ...account,
      id: accounts!.data[0].id,
    });

    if (accRes.error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));

      return false;
    }

    return true;
  };

  const save = async (values: FieldValues) => {
    try {
      setIsSaving(true);
      const { firstName, lastName, ...account } = values;

      const userUpdated = await updateUserDetails({ firstName, lastName });

      if (!userUpdated) {
        return;
      }

      const accountUpdated = await updateAccountDetails(account);

      if (accountUpdated) {
        dispatch(
          setMessage({
            content: t(`myAccount.updateSuccessMessage`),
            severity: "success",
          }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (email) {
      emailForm.reset({ email });
    }
  }, [email, emailForm]);

  useEffect(() => {
    if (isSaving) {
      return;
    }

    const account = accounts?.data?.[0];

    reset({
      ...account,
      ...user,
    });
  }, [accounts, user, reset, isSaving]);

  return (
      <Box display="flex" flexDirection="column">

        <form
          onSubmit={(e) => {
            clearErrors();
            return handleSubmit(save)(e);
          }}
        >
          <HomeForm>
            {!accounts?.data?.length ? (
              <Loader />
            ) : (
              <FormContainer>
                <EmailForm form={emailForm} />
                <AccountForm form={accountForm} />
              </FormContainer>
            )}
          </HomeForm>

          <HomeFormButtons>
            <TextIconButton
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              size="large"
              icon={SaveOutlinedIcon}
              text={t("myAccount.saveButton")}
            />
          </HomeFormButtons>
        </form>
      </Box>
  );
};

export default MyAccount;
